import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { 
  Star, Award, Sparkles, Lightbulb, Users, Heart, 
  Rocket, Trophy, Target, Zap, Loader2, CheckCircle,
  Brain, HandHeart, Palette, MessageCircle, TrendingUp,
  UsersRound, Crown, Mountain
} from 'lucide-react';
import { toast } from 'sonner';

const categoryIcons = {
  problem_solving: Brain,
  helping_others: HandHeart,
  exemplary_behavior: Star,
  creativity: Palette,
  participation: MessageCircle,
  improvement: TrendingUp,
  teamwork: UsersRound,
  leadership: Crown,
  persistence: Mountain,
  other: Award
};

const categoryColors = {
  problem_solving: 'bg-purple-100 text-purple-800',
  helping_others: 'bg-pink-100 text-pink-800',
  exemplary_behavior: 'bg-yellow-100 text-yellow-800',
  creativity: 'bg-indigo-100 text-indigo-800',
  participation: 'bg-blue-100 text-blue-800',
  improvement: 'bg-green-100 text-green-800',
  teamwork: 'bg-teal-100 text-teal-800',
  leadership: 'bg-orange-100 text-orange-800',
  persistence: 'bg-red-100 text-red-800',
  other: 'bg-gray-100 text-gray-800'
};

const quickPoints = [5, 10, 15, 20, 25];

export default function InstantRewardDialog({ 
  open, 
  onOpenChange, 
  sectionId, 
  sessionNumber,
  students = [],
  onRewardGiven 
}) {
  const { api } = useAuth();
  const [categories, setCategories] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('problem_solving');
  const [points, setPoints] = useState(10);
  const [note, setNote] = useState('');
  const [awarding, setAwarding] = useState(false);
  const [recentRewards, setRecentRewards] = useState([]);

  useEffect(() => {
    if (open) {
      fetchCategories();
      if (sectionId) {
        fetchRecentRewards();
      }
    }
  }, [open, sectionId]);

  const fetchCategories = async () => {
    try {
      const res = await api.get('/points/instant_reward_categories/');
      setCategories(res.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchRecentRewards = async () => {
    try {
      const res = await api.get(`/points/section/${sectionId}/rewards/`);
      setRecentRewards(res.data?.slice(0, 5) || []);
    } catch (error) {
      console.error('Failed to fetch recent rewards:', error);
    }
  };

  const handleAwardPoints = async () => {
    if (!selectedStudent) {
      toast.error('Please select a student');
      return;
    }

    setAwarding(true);
    try {
      const res = await api.post('/points/instant_reward/', {
        learner_id: selectedStudent,
        section_id: sectionId,
        amount: points,
        category: selectedCategory,
        note: note.trim() || null,
        session_number: sessionNumber
      });

      toast.success(
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-yellow-500" />
          <span>{res.data.message}</span>
        </div>
      );

      // Reset form
      setSelectedStudent('');
      setNote('');
      setPoints(10);
      
      // Refresh recent rewards
      fetchRecentRewards();
      
      // Callback
      if (onRewardGiven) {
        onRewardGiven(res.data);
      }
    } catch (error) {
      console.error('Failed to award points:', error);
      toast.error(error.response?.data?.detail || 'Failed to award points');
    } finally {
      setAwarding(false);
    }
  };

  const CategoryIcon = categoryIcons[selectedCategory] || Award;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-yellow-500" />
            Award Instant Points
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Student Selection */}
          <div className="space-y-2">
            <Label>Select Student *</Label>
            <Select value={selectedStudent} onValueChange={setSelectedStudent}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a student..." />
              </SelectTrigger>
              <SelectContent>
                {students.map(student => (
                  <SelectItem key={student.id} value={student.id}>
                    {student.first_name} {student.last_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Category Selection */}
          <div className="space-y-2">
            <Label>Reason Category</Label>
            <div className="grid grid-cols-2 gap-2">
              {categories.map(cat => {
                const Icon = categoryIcons[cat.value] || Award;
                const isSelected = selectedCategory === cat.value;
                return (
                  <Button
                    key={cat.value}
                    type="button"
                    variant={isSelected ? 'default' : 'outline'}
                    className={`justify-start h-auto py-2 ${isSelected ? '' : 'hover:bg-muted'}`}
                    onClick={() => setSelectedCategory(cat.value)}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    <span className="text-xs">{cat.label}</span>
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Quick Points Selection */}
          <div className="space-y-2">
            <Label>Points to Award</Label>
            <div className="flex gap-2">
              {quickPoints.map(p => (
                <Button
                  key={p}
                  type="button"
                  variant={points === p ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPoints(p)}
                  className="flex-1"
                >
                  {p}
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-muted-foreground">Custom:</span>
              <Input
                type="number"
                min={1}
                max={50}
                value={points}
                onChange={(e) => setPoints(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                className="w-20"
              />
              <span className="text-sm text-muted-foreground">points (1-50)</span>
            </div>
          </div>

          {/* Optional Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Additional Note (Optional)</Label>
            <Textarea
              id="note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g., Solved the bonus challenge in record time!"
              rows={2}
            />
          </div>

          {/* Preview */}
          {selectedStudent && (
            <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                  <CategoryIcon className="h-6 w-6 text-yellow-600" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">
                    {students.find(s => s.id === selectedStudent)?.first_name || 'Student'} will receive
                  </p>
                  <p className="text-2xl font-bold text-yellow-600">+{points} points</p>
                  <Badge className={categoryColors[selectedCategory]}>
                    {categories.find(c => c.value === selectedCategory)?.label || selectedCategory}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Recent Rewards */}
          {recentRewards.length > 0 && (
            <div className="space-y-2">
              <Label className="text-muted-foreground">Recent Awards This Session</Label>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {recentRewards.map(reward => {
                  const Icon = categoryIcons[reward.category] || Award;
                  return (
                    <div key={reward.id} className="flex items-center gap-2 text-sm p-2 bg-muted/50 rounded">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{reward.learner_name}</span>
                      <span className="text-muted-foreground">+{reward.amount} pts</span>
                      <Badge variant="outline" className="text-xs ml-auto">
                        {reward.category_display}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleAwardPoints} 
            disabled={awarding || !selectedStudent}
            className="bg-yellow-500 hover:bg-yellow-600 text-white"
          >
            {awarding ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4 mr-2" />
            )}
            Award {points} Points
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
